import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight, Sparkles, Zap, Flame, Inbox, ListTodo, Activity as ActivityIcon,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { useLeads, useLeadsLoaded } from '../lib/leadsStore';
import { useAuth } from '../lib/authStore';
import { normalizeStageKey } from '../components/LoanStageBadge';

const STAGES = [
  { key: 'ready_to_submit',          label: 'Ready' },
  { key: 'submitted_to_uw',          label: 'Submitted' },
  { key: 'approved_with_conditions', label: 'Approved' },
  { key: 'clear_to_close',           label: 'CTC' },
  { key: 'funded',                   label: 'Funded' },
];
const STAGE_KEYS = new Set(STAGES.map((s) => s.key));

function money(n, compact = true) {
  if (!n || n === 0) return '$0';
  if (compact && Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (compact && Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toLocaleString('en-US')}`;
}

function loanAmountOf(l) {
  const raw = l.application?.loanInfo?.loanAmount
    ?? l.application?.loanAmount
    ?? l.estAmount
    ?? l.loanAmount
    ?? 0;
  const n = Number(String(raw).replace(/[$,]/g, ''));
  return isNaN(n) ? 0 : n;
}

function greetingTimeOfDay() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}

export default function Dashboard() {
  const leads = useLeads();
  const loaded = useLeadsLoaded();
  const { user } = useAuth();

  const stats = useMemo(() => {
    const list = Array.isArray(leads) ? leads : [];
    const hot = list.filter((l) => l.temp === 'hot').length;

    const stageBuckets = STAGES.map((s) => ({ ...s, count: 0, volume: 0 }));
    let activeLoans = 0;
    let pipelineVolume = 0;
    let fundedMtdVolume = 0;
    let fundedMtdCount = 0;

    const now = new Date();
    const monthKey = `${now.getFullYear()}-${now.getMonth()}`;

    for (const l of list) {
      const stage = normalizeStageKey(l.loanStatus);
      if (!STAGE_KEYS.has(stage)) continue;
      const amt = loanAmountOf(l);
      const idx = STAGES.findIndex((s) => s.key === stage);
      stageBuckets[idx].count += 1;
      stageBuckets[idx].volume += amt;
      if (stage !== 'funded') {
        activeLoans += 1;
        pipelineVolume += amt;
      }
      if (stage === 'funded') {
        const fd = l.fundedAt || l.updatedAt || l.createdAt;
        if (fd) {
          const d = new Date(fd);
          if (`${d.getFullYear()}-${d.getMonth()}` === monthKey) {
            fundedMtdVolume += amt;
            fundedMtdCount += 1;
          }
        }
      }
    }

    return {
      total: list.length,
      hot,
      activeLoans,
      pipelineVolume,
      fundedMtdVolume,
      fundedMtdCount,
      ctcCount: stageBuckets.find((s) => s.key === 'clear_to_close')?.count || 0,
      stageBuckets,
    };
  }, [leads]);

  const empty = loaded && stats.total === 0;
  const firstName = (user?.name || '').split(' ')[0] || 'there';

  return (
    <div className="space-y-6 max-w-[1500px] mx-auto">
      <HeroBanner stats={stats} empty={empty} firstName={firstName} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 rise-in-stagger">
        <KpiTile label="Pipeline Volume" value={money(stats.pipelineVolume)} sub={`${stats.activeLoans} active loans`} />
        <KpiTile label="Active Loans"    value={stats.activeLoans}           sub="in the pipeline" />
        <KpiTile label="Funded MTD"      value={money(stats.fundedMtdVolume)} sub={`${stats.fundedMtdCount} units closed`} />
        <KpiTile label="Hot Leads"       value={stats.hot}                    sub="ready to call" tone="gold" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
        <div className="xl:col-span-8 space-y-5">
          <PipelineOverview buckets={stats.stageBuckets} total={stats.total} />
        </div>
        <div className="xl:col-span-4 space-y-5">
          <TodayTasks />
          <RecentActivity />
        </div>
      </div>
    </div>
  );
}

function HeroBanner({ stats, empty, firstName }) {
  const tod = greetingTimeOfDay();
  const headline = empty
    ? <>Welcome to your <span className="gold-text">Kinfolk workspace</span>.</>
    : stats.hot > 0
      ? <>You have <span className="gold-text">{stats.hot} hot {stats.hot === 1 ? 'lead' : 'leads'}</span> waiting on a call.</>
      : stats.ctcCount > 0
        ? <>{stats.ctcCount} {stats.ctcCount === 1 ? 'loan is' : 'loans are'} <span className="gold-text">ready to close</span>.</>
        : <>Your pipeline is <span className="gold-text">clean</span>. Time to plant the next seed.</>;

  const sub = empty
    ? 'No leads yet — bring one in from the Mailer or capture one through the public Apply form to get started.'
    : 'Live pipeline status. All numbers below are computed from your real leads in real time.';

  return (
    <div className="relative overflow-hidden rounded-2xl bg-neutral-900 text-white border hairline noise">
      <div className="absolute inset-0 bg-grid-dark" />
      <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-[hsl(var(--kf-gold))]/30 blur-[100px]" />
      <div className="relative px-7 py-8 md:px-10 md:py-10 flex flex-wrap items-center justify-between gap-6">
        <div className="max-w-2xl">
          <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.22em] text-white/50 font-semibold">
            <Sparkles className="h-3 w-3 text-[hsl(var(--kf-gold))]" />
            Good {tod}, {firstName}
          </div>
          <h1 className="mt-3 text-[24px] md:text-[28px] font-bold leading-tight tracking-tight">
            {headline}
          </h1>
          <p className="mt-2 text-[12.5px] text-white/60 max-w-xl">{sub}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to="/leads" className="btn-gold h-9 px-4 rounded-md text-[12.5px] font-semibold flex items-center gap-1.5">
              <Zap className="h-4 w-4" strokeWidth={2.5} /> {empty ? 'Open Leads' : 'Start Power Hour'}
            </Link>
            <Link to="/pipeline" className="h-9 px-4 rounded-md border border-white/20 hover:border-white/40 text-[12.5px] font-semibold flex items-center gap-1.5">
              View pipeline <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 min-w-[340px]">
          <Link to="/leads"><HeroStat label="Hot leads" value={stats.hot} sub="ready to call" tone={stats.hot > 0 ? 'gold' : undefined} /></Link>
          <HeroStat label="In pipeline" value={stats.activeLoans} sub="active loans" />
          <HeroStat label="CTC" value={stats.ctcCount} sub="clear to close" />
        </div>
      </div>
    </div>
  );
}

function HeroStat({ label, value, sub, tone }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] backdrop-blur px-3.5 py-3">
      <div className="text-[9.5px] uppercase tracking-[0.18em] text-white/50 font-bold">{label}</div>
      <div className={cn('mt-0.5 text-[18px] font-bold tracking-tight tabular-nums',
        tone === 'gold' && 'text-[hsl(var(--kf-gold))]',
        !tone && 'text-white')}>{value ?? 0}</div>
      <div className="text-[10px] text-white/50">{sub}</div>
    </div>
  );
}

function KpiTile({ label, value, sub, tone }) {
  return (
    <div className="relative overflow-hidden rounded-2xl border hairline bg-white dark:bg-neutral-900 p-5 card-lift group">
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-neutral-100 dark:bg-neutral-800">
        <div className="h-full w-2/3 sweep-line" />
      </div>
      <div className="text-[10.5px] uppercase tracking-[0.18em] text-neutral-500 font-bold">{label}</div>
      <div className={cn('mt-2 text-[22px] font-bold tracking-tight tabular-nums',
        tone === 'gold' ? 'text-[hsl(var(--kf-gold-deep))]' : 'text-neutral-900 dark:text-neutral-100')}>
        {value}
      </div>
      <div className="text-[11px] text-neutral-500">{sub}</div>
    </div>
  );
}

function PipelineOverview({ buckets, total }) {
  const max = Math.max(1, ...buckets.map((b) => b.count));
  const totalActive = buckets.reduce((s, b) => s + b.count, 0);
  const isEmpty = totalActive === 0;

  return (
    <div className="rounded-2xl border hairline bg-white dark:bg-neutral-900 p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-neutral-500 font-bold">Pipeline</div>
          <h3 className="text-[16px] font-bold tracking-tight text-neutral-900 dark:text-neutral-100">Stage distribution</h3>
          <p className="text-[12px] text-neutral-500 mt-0.5">
            {isEmpty ? 'No loans in pipeline yet.' : `${totalActive} ${totalActive === 1 ? 'loan' : 'loans'} across ${buckets.filter((b) => b.count).length} stage(s)`}
          </p>
        </div>
        <Link to="/pipeline" className="text-[12px] font-semibold text-neutral-900 dark:text-neutral-100 gold-underline">Open board →</Link>
      </div>

      {isEmpty ? (
        <EmptyBlock
          Icon={Inbox}
          title="Pipeline is empty"
          body={total > 0
            ? 'You have leads, but none are in a pipeline stage yet. Move a lead to "Ready to be Submitted" to see it here.'
            : 'Once leads are added and moved into the pipeline, you\'ll see the live stage distribution here.'}
          cta={{ to: '/pipeline', label: 'Open pipeline board' }}
        />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {buckets.map((b, i) => (
            <div key={b.key} className="rounded-lg border hairline p-3 card-lift">
              <div className="flex items-center gap-1.5">
                <span className={cn('h-1.5 w-1.5 rounded-full',
                  i === buckets.length - 1 ? 'bg-emerald-500'
                    : i >= 2 ? 'bg-[hsl(var(--kf-gold))]'
                    : 'bg-neutral-900 dark:bg-neutral-200')} />
                <span className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold truncate">{b.label}</span>
              </div>
              <div className="mt-1 text-[15px] font-bold text-neutral-900 dark:text-neutral-100 tabular-nums">{b.count}</div>
              <div className="text-[10px] text-neutral-500 tabular-nums">{money(b.volume)}</div>
              <div className="mt-2 h-1 rounded-full bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
                <div className={cn('h-full rounded-full',
                  i === buckets.length - 1 ? 'bg-emerald-500'
                    : i >= 2 ? 'bg-[hsl(var(--kf-gold))]'
                    : 'bg-neutral-900 dark:bg-neutral-200')}
                  style={{ width: `${(b.count / max) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TodayTasks() {
  return (
    <div className="rounded-2xl border hairline bg-white dark:bg-neutral-900 p-6">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-neutral-500 font-bold">Today</div>
          <h3 className="text-[15px] font-bold tracking-tight text-neutral-900 dark:text-neutral-100">Your tasks</h3>
        </div>
      </div>
      <EmptyBlock
        Icon={ListTodo}
        title="No tasks scheduled"
        body="Wingman will surface follow-ups, callbacks, and appointments here once you start scheduling them on lead profiles."
      />
    </div>
  );
}

function RecentActivity() {
  return (
    <div className="rounded-2xl border hairline bg-white dark:bg-neutral-900 p-6">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-neutral-500 font-bold">Activity</div>
          <h3 className="text-[15px] font-bold tracking-tight text-neutral-900 dark:text-neutral-100">Live stream</h3>
        </div>
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-neutral-500 bg-neutral-100 dark:bg-neutral-800 border hairline rounded px-1.5 py-0.5 font-bold">
          <span className="h-1.5 w-1.5 rounded-full bg-neutral-400" /> Idle
        </span>
      </div>
      <EmptyBlock
        Icon={ActivityIcon}
        title="No recent activity"
        body="Lead updates, status changes, and team actions will appear here as they happen."
      />
    </div>
  );
}

function EmptyBlock({ Icon = Flame, title, body, cta }) {
  return (
    <div className="text-center py-8 px-4">
      <div className="mx-auto h-10 w-10 rounded-xl border hairline bg-neutral-50 dark:bg-neutral-800/50 flex items-center justify-center">
        <Icon className="h-4 w-4 text-neutral-500" />
      </div>
      <div className="mt-3 text-[13px] font-bold text-neutral-800 dark:text-neutral-200">{title}</div>
      <p className="mt-1 text-[11.5px] text-neutral-500 max-w-xs mx-auto leading-relaxed">{body}</p>
      {cta && (
        <Link to={cta.to} className="inline-flex items-center gap-1 mt-3 text-[11.5px] font-semibold text-neutral-900 dark:text-neutral-100 gold-underline">
          {cta.label} <ArrowUpRight className="h-3 w-3" />
        </Link>
      )}
    </div>
  );
}
